import { useMemo, useState } from "react";
import WideDrawer from "../../../../../components/general/WideDrawer.tsx";
import { SecondaryButton } from "../../../../../components/general/SecondaryButton.tsx";
import { DocumentPreview } from "../../../../../views/DocumentDrawer/components/DocumentPreview.tsx";
import type { DocumentContent } from "../../../../../views/DocumentDrawer/components/DocumentPreview.tsx";
import type { GetDocumentResponse } from "../outstandingBill/OutstandingBills.tsx";
import type {
    SalesInvoiceRow,
    SalesInvoicesSummary,
    SalesInvoiceTabId,
} from "./types/salesInvoice";
import {
    computeTabCounts,
    EMPTY_MESSAGES,
    filterInvoicesByTab,
} from "./utils/salesInvoicesFormat";
import { InvoiceFilterBar } from "../invoiceFilters/InvoiceFilterBar.tsx";
import {
    InvoiceDateFieldEnum,
    type InvoiceDateFieldOption,
    type InvoiceFilterAccessors,
    type InvoiceFilterState,
} from "../invoiceFilters/types/invoiceFilter.ts";
import {
    collectRelationOptions,
    createEmptyInvoiceFilterState,
    FILTERED_EMPTY_MESSAGE,
    filterInvoiceRows,
    isInvoiceFilterActive,
} from "../invoiceFilters/utils/invoiceFilters.ts";
import { SalesInvoiceStatCards } from "./SalesInvoiceStatCards.tsx";
import { SalesInvoiceTabs } from "./SalesInvoiceTabs.tsx";
import { SalesInvoicesTable } from "./SalesInvoicesTable.tsx";

interface Props {
    invoices: SalesInvoiceRow[];
    summary: SalesInvoicesSummary | null;
    isLoading: boolean;
    onFetchDocument: (invoice: SalesInvoiceRow) => Promise<GetDocumentResponse | null>;
    onReportIncorrect: (invoice: SalesInvoiceRow) => Promise<void>;
    onOpenInvoice: (invoice: SalesInvoiceRow) => void;
}

const CUSTOMER_LABEL = "Klant";
const ALL_CUSTOMERS_PLACEHOLDER = "Alle klanten";
const PERIOD_LABEL = "Periode";
const INVOICE_DATE_LABEL = "Factuurdatum";
const DUE_DATE_LABEL = "Vervaldatum";

const DATE_FIELD_OPTIONS: InvoiceDateFieldOption[] = [
    { value: InvoiceDateFieldEnum.INVOICE_DATE, label: INVOICE_DATE_LABEL },
    { value: InvoiceDateFieldEnum.DUE_DATE, label: DUE_DATE_LABEL },
];

const INVOICE_FILTER_ACCESSORS: InvoiceFilterAccessors<SalesInvoiceRow> = {
    getRelation: (invoice) => invoice.customer_name,
    getDate: (invoice, dateField) =>
        dateField === InvoiceDateFieldEnum.DUE_DATE ? invoice.due_date : invoice.invoice_date,
};

function PreviewSkeleton() {
    return <div className="h-96 animate-pulse rounded-lg bg-gray-100" />;
}

export function SalesInvoicesOverview({
    invoices,
    summary,
    isLoading,
    onFetchDocument,
    onReportIncorrect,
    onOpenInvoice,
}: Props) {
    const [activeTab, setActiveTab] = useState<SalesInvoiceTabId>("alle");
    const [selectedInvoice, setSelectedInvoice] = useState<SalesInvoiceRow | null>(null);
    const [previewDoc, setPreviewDoc] = useState<DocumentContent | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [filters, setFilters] = useState<InvoiceFilterState>(() =>
        createEmptyInvoiceFilterState(InvoiceDateFieldEnum.INVOICE_DATE)
    );

    const relationOptions = useMemo(
        () => collectRelationOptions(invoices, (invoice) => invoice.customer_name),
        [invoices]
    );
    const filteredInvoices = useMemo(
        () => filterInvoiceRows(invoices, filters, INVOICE_FILTER_ACCESSORS),
        [invoices, filters]
    );
    const isFiltered = isInvoiceFilterActive(filters);

    const tabCounts = useMemo(
        () => (isLoading ? null : computeTabCounts(filteredInvoices)),
        [filteredInvoices, isLoading]
    );
    const visibleInvoices = useMemo(
        () => filterInvoicesByTab(filteredInvoices, activeTab),
        [filteredInvoices, activeTab]
    );

    async function handleRowClick(invoice: SalesInvoiceRow) {
        if (invoice.source === "created") {
            onOpenInvoice(invoice);
            return;
        }
        setSelectedInvoice(invoice);
        setPreviewDoc(null);
        setIsDrawerOpen(true);
        if (!invoice.document_id) {
            return;
        }
        setIsPreviewLoading(true);
        const data = await onFetchDocument(invoice);
        if (data) {
            setPreviewDoc({
                file_name: data.file_name,
                file_blob: data.file_blob,
                mimetype: data.mimetype,
            });
        }
        setIsPreviewLoading(false);
    }

    async function handleReportIncorrect() {
        if (!selectedInvoice) return;
        await onReportIncorrect(selectedInvoice);
        setIsDrawerOpen(false);
        setSelectedInvoice(null);
        setPreviewDoc(null);
    }

    return (
        <div className="flex flex-col gap-8">
            <SalesInvoiceStatCards summary={summary} isLoading={isLoading} />
            <div>
                <SalesInvoiceTabs
                    activeTab={activeTab}
                    counts={tabCounts}
                    onChange={setActiveTab}
                />
                <div className="mt-4">
                    <InvoiceFilterBar
                        value={filters}
                        onChange={setFilters}
                        relationOptions={relationOptions}
                        relationLabel={CUSTOMER_LABEL}
                        relationPlaceholder={ALL_CUSTOMERS_PLACEHOLDER}
                        dateLabel={PERIOD_LABEL}
                        dateFieldOptions={DATE_FIELD_OPTIONS}
                    />
                </div>
                <SalesInvoicesTable
                    invoices={visibleInvoices}
                    isLoading={isLoading}
                    emptyMessage={isFiltered ? FILTERED_EMPTY_MESSAGE : EMPTY_MESSAGES[activeTab]}
                    onRowClick={handleRowClick}
                />
            </div>
            <WideDrawer
                open={isDrawerOpen}
                updateOpenState={(open: boolean) => {
                    if (!open) {
                        setSelectedInvoice(null);
                        setPreviewDoc(null);
                    }
                    setIsDrawerOpen(open);
                }}
                title={
                    selectedInvoice?.file_name ??
                    (selectedInvoice?.invoice_number
                        ? `Factuur ${selectedInvoice.invoice_number}`
                        : "Factuur")
                }
            >
                {selectedInvoice?.source === "uploaded" && (
                    <div className="flex flex-col gap-4">
                        {isPreviewLoading && <PreviewSkeleton />}
                        {previewDoc && <DocumentPreview document={previewDoc} />}
                        <div className="flex justify-end border-t border-gray-100 pt-4">
                            <SecondaryButton
                                title="Dit klopt niet"
                                onClick={handleReportIncorrect}
                            />
                        </div>
                    </div>
                )}
            </WideDrawer>
        </div>
    );
}
